import { useMemo } from 'react';
import { SelectedFilters, useContactTaskListStore, useTaskCenterStore } from '@frontend/task-center-scope';

interface FilterState {
  selectedFilters: SelectedFilters;
  setFilters: (filters: SelectedFilters) => void;
}

/**
 * Hook that conditionally uses either useTaskCenterStore or useContactTaskListStore
 * based on availability. Prefers ContactTaskListStore when available (contact panel context),
 * falls back to TaskCenterStore (task center panel context).
 */
export const useGlobalFilterState = (): FilterState => {
  // Try to get contact task list store first (contact panel context)
  const contactPanelFilterInfo = useContactTaskListStore((s) => ({
    selectedFilters: s?.selectedFilters,
    setFilters: s?.setFilters,
  }));

  // Get task center store as fallback (task center panel context)
  const taskCenterFilterInfo = useTaskCenterStore(['selectedFilters', 'setFilters']);

  return useMemo(() => {
    // If contact panel store is available and has the required data, use it
    if (contactPanelFilterInfo?.selectedFilters && contactPanelFilterInfo?.setFilters) {
      return {
        selectedFilters: contactPanelFilterInfo.selectedFilters,
        setFilters: contactPanelFilterInfo.setFilters,
      };
    }

    // Otherwise, use task center store
    return {
      selectedFilters: taskCenterFilterInfo.selectedFilters,
      setFilters: taskCenterFilterInfo.setFilters,
    };
  }, [contactPanelFilterInfo, taskCenterFilterInfo]);
};
